// @todo: Migrate to @wandb/components Dialog.Alert once these issues are resolved:
// 1. Dark mode: the overlay's --reverse-app-filter inverts the backdrop dimmer,
//    making it invisible against a dark background.
// 2. Dark mode: the modal surface (moon-900) has no visible border, relying solely
//    on box-shadow which is too subtle against the dark page.
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Dialog from '@wandb/weave/components/Dialog';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {
  SlurmCommandType,
  useSubmitSlurmCommandMutation,
} from '../../generated/graphql';
import {
  extractStatusCodeFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';

const VARIANT_CONFIG = {
  cancel: {
    title: 'Cancel this Slurm job?',
    description: (jobId: string) =>
      `Stopping this Slurm job, "${jobId}" will terminate all active processes and any runs currently in progress. This action cannot be undone.`,
    confirmLabel: 'Cancel Slurm job',
    toastMessage: (jobId: string) => `Cancel requested for ${jobId}`,
    command: SlurmCommandType.Cancel,
  },
  requeue: {
    title: 'Requeue this Slurm job?',
    description: (jobId: string) =>
      `This Slurm job, "${jobId}" will terminate all active processes and any runs currently in progress, then be added back to the queue to run again from the beginning. Any progress from the current execution will not be saved.`,
    confirmLabel: 'Requeue Slurm job',
    toastMessage: (jobId: string) => `Requeue requested for ${jobId}`,
    command: SlurmCommandType.Requeue,
  },
} as const;

interface SlurmJobConfirmModalProps {
  isOpen: boolean;
  jobId: string;
  entityName: string;
  projectName: string;
  runName: string;
  variant: 'cancel' | 'requeue';
  onClose: () => void;
}

export const SlurmJobConfirmModal: React.FC<SlurmJobConfirmModalProps> =
  React.memo(
    ({isOpen, jobId, entityName, projectName, runName, variant, onClose}) => {
      const config = VARIANT_CONFIG[variant];
      const [submitSlurmCommand] = useSubmitSlurmCommandMutation({
        context: propagateErrorsContext(),
      });

      const handleConfirm = () => {
        submitSlurmCommand({
          variables: {
            entityName,
            projectName,
            runName,
            command: config.command,
          },
        })
          .then(() => {
            toast(config.toastMessage(jobId));
          })
          .catch((error: unknown) => {
            if (extractStatusCodeFromApolloError(error) === 409) {
              toast(
                `A ${variant} is already in flight for ${jobId}. Give it a moment and try again.`,
                {type: 'info'}
              );
              return;
            }
            const serverMessage = (
              error as {graphQLErrors?: Array<{message?: string}>} | undefined
            )?.graphQLErrors?.[0]?.message;
            toast(serverMessage ?? `Failed to ${variant} Slurm job ${jobId}`, {
              type: 'error',
            });
          });
        onClose();
      };

      return (
        <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
          <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content
              className="w-[540px] rounded-lg"
              onInteractOutside={e => {
                e.preventDefault();
              }}>
              <Dialog.Title>{config.title}</Dialog.Title>
              <Dialog.Description className="mt-16">
                {config.description(jobId)}
              </Dialog.Description>
              <div className="mb-8 mt-24 flex items-center">
                <Button
                  variant="destructive"
                  size="large"
                  onClick={handleConfirm}>
                  {config.confirmLabel}
                </Button>
                <Dialog.Close className="ml-8" asChild>
                  <Button variant="ghost" size="large">
                    Cancel
                  </Button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      );
    }
  );
